const uniqid = require('uniqid');

// https://helloacm.com/common-javascript-functions-for-financial-calculations/

/***********************************************
 *              Present Value                  *
 * pv = fv / (1 + (rate / freq))^periods       *
 * pv = Present Value                          *
 * fv = Future Value                           *
 * rate = interest rate (expressed as %)       *
 * freq = compounding frequency                *
 * periods = number of periods until maturity  *
 ***********************************************/
function presentValue(fv, freq, rate, periods) {
    return (fv / Math.pow((1 + (rate / 100 / freq)), periods));
}

/************************************************
*                Future Value                  *
* fv = pv * (1 + (rate / freq))^periods        *
* fv = Future Value                            *
* pv = Present Value                           *
* rate = interest rate (expressed as %)        *
* freq = compounding frequency                 *
* periods = number of periods until maturity   *
************************************************/
function futureValue(pv, freq, rate, periods) {
    return (pv * Math.pow(1 + (rate / 100 / freq), periods));
}
/************************************************
 *            Annualized Return                 *
 * r = (fv - pv) / pv / years                   *
 * fv = future value                            *
 * pv = present value                           *
 * years = term of loan in years                *
 ************************************************/
function annualizedReturn(fv, pv, years) {
    return (fv - pv) / pv / years;
}

function monthlyPayment(pv, freq, rate, periods) {
    rate = rate / 100 / freq;

    var x = Math.pow(1 + rate, periods);
    return (pv * x * rate) / (x - 1);
}

/***********************************************
 *                 Annuity                     *
 * a = fv / (((1 + r / c)^n) - 1) / (r/c)      *
 * fv = future value                           *
 * r = interest rate                           *
 * c = compounding frequency                   *
 * n = total number of periods                 *
 ***********************************************/
function annuity(fv, freq, rate, periods) {
    rate = rate / 100 / freq;
    return (fv / ((Math.pow(1 + rate, periods) - 1)) * rate);
}

function calcAmortPrincipal(pymt, freq, rate, periods) {
    rate = rate / 100 / freq;
    return (pymt * (1 - (1 / Math.pow(1 + rate, periods))) / rate);
}

function regularDeposit(payment, freq, rate, periods) {
    rate = rate / 100 / freq;
    return (payment * (Math.pow(1 + rate, periods) - 1) / rate * (1 + rate));
}

/***********************************************
 *  Convert to currency notation               *
 ***********************************************/
function toCurrency(num) {
    num = Math.round(num * 100) * 0.01;
    var currstring = num.toString();
    if (currstring.match(/\./)) {
        var curr = currstring.split('.');
    } else {
        var curr = [currstring, "00"];
    }
    curr[1] += "00";
    curr[2] = "";
    var returnval = "";
    var length = curr[0].length;

    // add 0 to decimal if necessary
    for (var i = 0; i < 2; i++)
        curr[2] += curr[1].substr(i, 1);

    // insert commas for readability
    for (i = length; (i - 3) > 0; i = i - 3) {
        returnval = "," + curr[0].substr(i - 3, 3) + returnval;
    }
    returnval = curr[0].substr(0, i) + returnval + "." + curr[2];
    return (returnval);
}

function FV(PV, interest, numberOfPayments) {
    var growthFactor = (1 + interest / 100)
    var FV = PV * (Math.pow(growthFactor, numberOfPayments))
    return FV;
}

/*
    Function to calculate debt snowball payoff and a summary page
*/
function calcDebtPayoff(debts, payoffOrder) {

    function payoffSchedule(debts, payoffOrder) {

        function paymentRec(debtName, debtId) {
            return {
                month: 0,
                debtName,
                debtId,
                regularPayment: 0.0,
                extraPayment: 0.0,
                totalPayment: 0.0,
                interest: 0.0,
                balance: 0.0,
                interestToDate: 0.0,
                paymentsToDate: 0.0
            }
        }

        // create empty paymet schedule
        let paymentSchedule = []

        // Create a new array that holds a copy of org array with added props
        let calcArray = debts.map((debt) => {

            // copy oject
            // only debtName, payment, balance and rate come from the org debts list
            let newElement = {
                id: uniqid(),
                debtName: debt.debtName,
                balance: debt.balance,
                rate: debt.rate,
                payment: debt.payment,
                payments: 0,
                calcPayment: parseFloat(debt.payment),
                calcBalance: parseFloat(debt.balance), // add a balance we can adjust without changing the amount
                snowballAmount: 0.0, // amount to apply to snowball.
                monthlyRate: (parseFloat(debt.rate) / 100.0) / 12.0, // pre calc the monthly interest rate
                interestToDate: 0.0,
                paymentsToDate: 0.0
            }

            return newElement
        })

        // sort new array by rate or balance
        calcArray.sort((first, second) => {

            let rtnValue = 0
            switch (payoffOrder) {

                case payoffOrder.ENTRY_ORDER:
                    rtnValue = 0 // don't sort
                    break;

                case payoffOrder.HIGH_INTEREST_FIRST:
                    if (first.rate > second.rate) {
                        rtnValue = 1
                    } else if (second.rate > first.rate) {
                        rtnValue = -1
                    }
                    break

                case payoffOrder.LOW_BAL_FIRST:
                    if (first.balance > second.balance) {
                        rtnValue = 1
                    } else if (second.balance > first.balance) {
                        rtnValue = -1
                    }
                    break;
            }
            return rtnValue
        })

        // calc each month with a cap of 100 years
        // loop until every balnance is 0
        for (let month = 1; !calcArray.every(debt => { return debt.calcBalance === 0.0 }) && month < 1200; month++) {

            // get the snowball amount
            let totalSnowballAmount = calcArray.reduce((accumulator, debt) => {
                return accumulator + debt.snowballAmount
            }, 0.0)

            // apply debt reduction payment to each debt
            for (let debt of calcArray) {

                // only calc payment if we still have a balance
                if (debt.calcBalance > 0.0) {

                    debt.payments++;

                    // we have a balance so create a payment record
                    let payment = paymentRec(debt.debtName, debt.id)

                    // set the payment month
                    payment.month = month

                    // calculate based on end of month
                    // calc the interest amount for this month
                    payment.interest = debt.calcBalance * debt.monthlyRate
                    payment.balance = debt.calcBalance + payment.interest

                    // add to total interest for debt
                    debt.interestToDate += payment.interest

                    // save the interest to date in the payment record
                    payment.interestToDate = debt.interestToDate

                    // this shoule be the same as adding the interest to the balance.
                    // use FV so it is more official.
                    const newBal = FV(debt.calcBalance, debt.rate / 12, 1)

                    // if payment is greater than balance apply balance and roll the diff, else apply payment
                    if (newBal <= debt.calcPayment) {

                        // we can pay off the balance with regular payment
                        // add the extra to the snowball amount
                        totalSnowballAmount += debt.calcPayment - newBal

                        // debt is paid off, the payment amount is now available for snowball
                        // for subsequent months
                        debt.snowballAmount = debt.calcPayment
                        debt.calcBalance = 0.0


                        payment.regularPayment = newBal
                        payment.extraPayment = 0.0
                        payment.totalPayment = newBal
                        payment.balance = 0.0

                        // update interest and payments to date
                        debt.paymentsToDate += payment.totalPayment
                        payment.paymentsToDate = debt.paymentsToDate

                    } else if (newBal <= debt.calcPayment + totalSnowballAmount) {

                        // we can pay off the balance with payment and snowball Amount
                        // take the extra needed from the snowball amount
                        totalSnowballAmount -= (newBal - debt.calcPayment)

                        // debt is paid off, the payment amount is now available for snowball
                        debt.snowballAmount = debt.calcPayment

                        debt.snowballPayment = newBal - debt.calcPayment
                        debt.calcBalance = 0.0

                        payment.regularPayment = debt.calcPayment
                        payment.extraPayment = newBal - debt.calcPayment
                        payment.totalPayment = payment.extraPayment + payment.regularPayment
                        payment.balance = 0.0

                        // update interest and payments to date
                        debt.paymentsToDate += payment.totalPayment
                        payment.paymentsToDate = debt.paymentsToDate

                    } else {

                        // we cant pay off this debt balance with the payment and snowball Amount
                        debt.calcBalance = newBal - (debt.calcPayment + totalSnowballAmount)
                        debt.snowballPayment = 0.0

                        payment.regularPayment = debt.calcPayment
                        payment.extraPayment = totalSnowballAmount
                        payment.totalPayment = payment.extraPayment + payment.regularPayment
                        payment.balance = debt.calcBalance

                        // update interest and payments to date
                        debt.paymentsToDate += payment.totalPayment
                        payment.paymentsToDate = debt.paymentsToDate

                        totalSnowballAmount = 0.0

                    }

                    // add payment to our payment schedule if we made a payment
                    if (payment.totalPayment > 0) {
                        paymentSchedule.push(payment)
                    }

                } // end if we have a balance
            }
        } // end of for each month

        return paymentSchedule

    }

    // group all like payments into sections with start month and end month
    function payoffSummary(payoffSchedule) {

        function summaryRec(startMonth) {
            return {
                startMonth,  // start month of group
                endMonth: 0, // end month of group
                recs: []
            }
        }

        const summarySchedule = []
        let monthsSummary = summaryRec(1) // create a summmary record for month 1

        // reduce all the payments into a new array
        // each months payments are grouped into their own array
        let monthlyPayments = payoffSchedule.reduce((result, payment) => {
            let offset = payment.month - 1

            // payments in the schedule are in month ascending order
            // soe we can get away with this
            if (result.length == offset) {
                result.push([payment])
            } else {
                result[offset].push(payment)
            }

            return result
        }, [])

        // go through all payment months and compare payments
        // start at index 1 and compare to previous month
        // go one past the end of the array so we capture the 
        // last set of changes
        for (let monthIndex = 1; monthIndex <= monthlyPayments.length; monthIndex++) {

            let lastMonthRecs = monthlyPayments[monthIndex - 1]
            let currentMonthRecs = monthIndex === monthlyPayments.length ? [] : monthlyPayments[monthIndex]


            // see if our payments are the same as last month
            // assume change is true.  Only false if payment existed last month and was the same total amount
            let changed = true

            for (let lastMonthRec of lastMonthRecs) {

                // find payment by id
                // check last month against this month to catch payments
                // that have dropped out.  last month will always be bigger or the same size.
                let currentMonthRec = currentMonthRecs.find(monthRec => {
                    return monthRec.debtId === lastMonthRec.debtId
                })

                if (currentMonthRec !== undefined) {

                    if (currentMonthRec.totalPayment === lastMonthRec.totalPayment) {
                        // payment is the same, no change
                        changed = false
                    }
                }

            } // end of for each current month rec

            // do we have a change or at the last month
            if (changed === true) {

                // we have a change

                // close current group
                // first pass monthSummary will still be null
                monthsSummary.recs = lastMonthRecs
                monthsSummary.endMonth = monthIndex

                summarySchedule.push(monthsSummary)

                // start new summary
                monthsSummary = summaryRec(monthIndex + 1)

            }

        }

        return summarySchedule
    }

    const schedule = payoffSchedule(debts, payoffOrder)
    const summary = payoffSummary(schedule)

    return {
        schedule,
        summary
    }
}

export {FV, calcDebtPayoff}
// module.exports.FV = FV
// module.exports.calcDebtPayoff = calcDebtPayoff
